import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react'
import { type RapportsBoitier, type SourceHistorique } from './api'
import type { Piste, PropsCarteRejeu } from './carteTypes'
import { csvArrets, csvSynthese, csvTrajets, nomFichier, versGpx } from './export'
import { avancer, bornesDe, interpoler, VITESSES_REJEU, dureeReelleRejeu, type BornesRejeu, type VitesseRejeu } from './lecteur'
import { PARAMETRES_DETECTION } from './parametres'
import {
  LIBELLE_PRESET, depuisChampLocal, formatDateHeure, formatDistance, formatDuree, formatHeure, formatHeureSeconde, formatJour, formatVitesse,
  periodePreset, verifierPeriode, versChampLocal, type PresetPeriode,
} from './periodes'
import type { Arret, EntreeFlotte, Historique, InfoHistorique, Trajet } from './types'
import './historique.css'

/**
 * Écran « Historique » : trajets passés d'un ou de plusieurs véhicules / livreurs / boîtiers sur une période, rejeu, rapports et exports.
 *
 * Même code dans le portail SaaS et la console opérateur : la console fournit (1) la liste de sa flotte (`flotte`), (2) la SOURCE de
 * données (appels réels ou aperçu fictif), (3) la carte avec son fond choisissable (`Carte`, chargée à la demande). Aucune logique de
 * détection ici : les rapports viennent de la base, qui voit toutes les positions (le navigateur n'en reçoit qu'un échantillon).
 */

const COULEURS_VEHICULES = ['#0f8a6a', '#2f6fde', '#b5179e', '#e0701b', '#6b4fd8'] as const
export const MAX_VEHICULES = COULEURS_VEHICULES.length
const COUPURE_S = PARAMETRES_DETECTION.trou_max_s
const LIBELLE_PALIER = { starter: 'Starter', pro: 'Pro', business: 'Business' } as const

type Genre = EntreeFlotte['genre']
const LIBELLE_GENRE: Record<Genre, string> = { vehicule: 'Véhicule', livreur: 'Livreur', boitier: 'Boîtier' }
const LIBELLE_GENRE_PL: Record<Genre, string> = { vehicule: 'Véhicules', livreur: 'Livreurs', boitier: 'Boîtiers' }

export interface PropsEcranHistorique {
  /** Source des données : réelle (appels à la base) ou fictive (aperçu). `null` tant qu'elle se prépare. */
  source: SourceHistorique | null
  /** La flotte de l'organisation, déjà ramenée à des entrées par véhicule, livreur et boîtier. */
  flotte: readonly EntreeFlotte[]
  /** Carte du rejeu avec le fond de la console (composant chargé à la demande). */
  Carte: ComponentType<PropsCarteRejeu>
  nomOrganisation: string
  /** Période affichée au premier chargement (défaut : aujourd'hui). */
  periodeInitiale?: PresetPeriode | undefined
  /** « Voir l'historique des alertes » : fourni seulement si la console y a droit. */
  surOuvrirAlertes?: (() => void) | undefined
  /** Écran lui-même verrouillé (palier, rôle) : message à montrer à la place. */
  indisponible?: ReactNode | undefined
}

/* ───────────────────────────── petites briques ───────────────────────────── */

const Ic = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="hg-ic">
    <path d={d} />
  </svg>
)
const ICONES = {
  lecture: 'M7 4.5v15l12-7.5z',
  pause: 'M8 5v14M16 5v14',
  debut: 'M6 5v14M18 5l-9 7 9 7z',
  fin: 'M18 5v14M6 5l9 7-9 7z',
  telecharger: 'M12 3v12m0 0l-4-4m4 4l4-4M5 20h14',
  imprimer: 'M7 9V4h10v5M7 17H5a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2M7 14h10v6H7z',
  info: 'M12 8v.01M11 12h1v5h1M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
  recharger: 'M4 12a8 8 0 0 1 14-5.3L20 9M20 4v5h-5M20 12a8 8 0 0 1-14 5.3L4 15M4 20v-5h5',
}

function telecharger(nom: string, contenu: string, type: string) {
  const blob = new Blob([contenu], { type })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = nom
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(a.href), 1000)
}

interface DonneesBoitier {
  historique: Historique
  rapports: RapportsBoitier
}
type ResultatBoitier = { ok: true; donnees: DonneesBoitier } | { ok: false; erreur: string }

/** Frise : une barre par véhicule — trajets, arrêts, absences de signal — et le curseur. Un clic saute à l'instant. */
function Frise({ debut, fin, rapports, t, surChoix }: { debut: number; fin: number; rapports: RapportsBoitier; t: number; surChoix: (t: number) => void }) {
  const duree = Math.max(fin - debut, 1)
  const pct = (x: number) => `${(Math.max(0, Math.min(1, (x - debut) / duree)) * 100).toFixed(3)}%`
  // Trajets et arrêts ; ce qui n'est ni l'un ni l'autre et qui dépasse l'absence de signal est hachuré.
  const segs: { d: number; f: number; c: string }[] = [
    ...rapports.trajets.map((x) => ({ d: x.debut, f: x.fin, c: 'hg-f-trajet' })),
    ...rapports.arrets.map((x) => ({ d: x.debut, f: x.fin, c: 'hg-f-arret' })),
  ]
  return (
    <div
      className="hg-frise"
      role="img"
      aria-label="Frise de la période : trajets, arrêts et absences de signal"
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect()
        surChoix(debut + ((e.clientX - r.left) / Math.max(r.width, 1)) * duree)
      }}
    >
      {segs.map((s, i) => (
        <span key={i} className={s.c} style={{ left: pct(s.d), width: `calc(${pct(s.f)} - ${pct(s.d)})` }} />
      ))}
      <span className="hg-f-curseur" style={{ left: pct(t) }} />
    </div>
  )
}

/* ───────────────────────────── l'écran ───────────────────────────── */

export function EcranHistorique({ source, flotte, Carte, nomOrganisation, surOuvrirAlertes, indisponible, periodeInitiale }: PropsEcranHistorique) {
  const [genre, setGenre] = useState<Genre>('vehicule')
  const [selection, setSelection] = useState<string[]>([])
  const [info, setInfo] = useState<InfoHistorique | null>(null)
  const [erreurInfo, setErreurInfo] = useState<string | null>(null)
  const [preset, setPreset] = useState<PresetPeriode | 'perso'>(periodeInitiale ?? 'aujourdhui')
  const [duChamp, setDuChamp] = useState('')
  const [auChamp, setAuChamp] = useState('')
  const [etat, setEtat] = useState<'repos' | 'chargement' | 'pret'>('repos')
  const [resultats, setResultats] = useState<Record<string, ResultatBoitier>>({})
  /* L'ordre du chargement = l'ordre de la sélection : la couleur d'un véhicule est la même sur sa pastille et sur son tracé. */
  const [ordre, setOrdre] = useState<string[]>([])
  const [messagesPeriode, setMessagesPeriode] = useState<string[]>([])
  const [erreurPeriode, setErreurPeriode] = useState<string | null>(null)
  const [periodeDemandee, setPeriodeDemandee] = useState<{ du: number; au: number } | null>(null)
  const [t, setT] = useState(0)
  const [lecture, setLecture] = useState(false)
  const [vitesse, setVitesse] = useState<VitesseRejeu>(10)
  const [accelerer, setAccelerer] = useState(true)
  const [suivre, setSuivre] = useState(true)
  const [onglet, setOnglet] = useState<'synthese' | 'trajets' | 'arrets'>('synthese')
  const [adresses, setAdresses] = useState<Record<string, string | null | 'chargement'>>({})
  const [cleCadrage, setCleCadrage] = useState('')
  const [arretOuvert, setArretOuvert] = useState<{ pisteId: string; n: number } | null>(null)
  const jeton = useRef(0)
  const tRef = useRef(0)

  /* ───── Entrées proposées ───── */
  const entrees = useMemo(() => flotte.filter((e) => e.genre === genre), [flotte, genre])
  const libelleDe = useCallback((id: string) => {
    const e = flotte.find((x) => x.boitierId === id && x.genre === genre) ?? flotte.find((x) => x.boitierId === id)
    return e ? e.libelle : 'Boîtier'
  }, [flotte, genre])

  /* ───── Informations de période (rétention du palier) ───── */
  useEffect(() => {
    if (!source) return
    let vivant = true
    source.info().then(
      (i) => vivant && setInfo(i),
      (e: unknown) => vivant && setErreurInfo(e instanceof Error ? e.message : "L'historique n'a pas pu être préparé."),
    )
    return () => {
      vivant = false
    }
  }, [source])

  /* ───── Sélection : par défaut le premier véhicule qui a un boîtier ; un choix qui n'existe plus est retiré ───── */
  useEffect(() => {
    setSelection((s) => {
      const valides = s.filter((id) => flotte.some((e) => e.boitierId === id && e.actif))
      if (valides.length > 0) return valides.length === s.length ? s : valides
      const premier = flotte.find((e) => e.actif && e.genre === 'vehicule') ?? flotte.find((e) => e.actif)
      return premier ? [premier.boitierId] : []
    })
  }, [flotte])

  /* ───── Période : valeurs des champs ───── */
  const maintenant = () => Date.now()
  useEffect(() => {
    if (preset === 'perso') return
    const p = periodePreset(preset, maintenant())
    setDuChamp(versChampLocal(p.du))
    setAuChamp(versChampLocal(p.au))
  }, [preset])

  /* ───── Chargement ───── */
  const charger = useCallback(
    async (ids: string[], du: number, au: number) => {
      if (!source || ids.length === 0 || !info) return
      const mon = ++jeton.current
      setEtat('chargement')
      setLecture(false)
      setArretOuvert(null)
      const sortie: Record<string, ResultatBoitier> = {}
      await Promise.all(
        ids.map(async (id) => {
          try {
            const [historique, rapports] = await Promise.all([source.historique(id, du, au, info.pointsDefaut), source.rapports(id, du, au)])
            sortie[id] = { ok: true, donnees: { historique, rapports } }
          } catch (e) {
            sortie[id] = { ok: false, erreur: e instanceof Error ? e.message : "Chargement impossible." }
          }
        }),
      )
      if (mon !== jeton.current) return // une demande plus récente a pris le relais
      setResultats(sortie)
      setOrdre(ids)
      setPeriodeDemandee({ du, au })
      setCleCadrage(`${mon}`)
      setEtat('pret')
      const debuts = Object.values(sortie).flatMap((r) => (r.ok && r.donnees.historique.points.length > 0 ? [r.donnees.historique.points[0]!.t] : []))
      const instant = debuts.length > 0 ? Math.min(...debuts) : du
      tRef.current = instant
      setT(instant)
    },
    [source, info],
  )

  const lancer = useCallback(() => {
    if (!info) return
    let du: number
    let au: number
    if (preset === 'perso') {
      const d = depuisChampLocal(duChamp)
      const a = depuisChampLocal(auChamp)
      const v = verifierPeriode(d, a, { maxJours: info.periodeMaxJours, retentionJours: info.retentionJours, maintenant: maintenant() })
      setMessagesPeriode(v.messages)
      if (!v.ok) {
        setErreurPeriode(v.messages[0] ?? 'Période invalide.')
        return
      }
      du = v.du
      au = v.au
    } else {
      const p = periodePreset(preset, maintenant())
      const v = verifierPeriode(p.du, p.au, { maxJours: info.periodeMaxJours, retentionJours: info.retentionJours, maintenant: maintenant() })
      setMessagesPeriode(v.messages)
      if (!v.ok) {
        setErreurPeriode(v.messages[0] ?? 'Période invalide.')
        return
      }
      du = v.du
      au = v.au
    }
    setErreurPeriode(null)
    void charger(selection, du / 1000, au / 1000)
  }, [info, preset, duChamp, auChamp, selection, charger])

  // Premier affichage : on charge la période par défaut dès que tout est prêt.
  const dejaCharge = useRef(false)
  useEffect(() => {
    if (!dejaCharge.current && info && source && selection.length > 0) {
      dejaCharge.current = true
      lancer()
    }
  }, [info, source, selection, lancer])

  /* ───── Données dérivées ───── */
  const ids = ordre
  const pistes = useMemo<Piste[]>(
    () =>
      ids.flatMap((id, i) => {
        const r = resultats[id]
        if (!r || !r.ok) return []
        return [{
          id, libelle: libelleDe(id), couleur: COULEURS_VEHICULES[i % COULEURS_VEHICULES.length]!, points: r.donnees.historique.points,
          arrets: r.donnees.rapports.arrets, trajets: r.donnees.rapports.trajets,
        }]
      }),
    [ids, resultats, libelleDe],
  )
  const bornes = useMemo<BornesRejeu | null>(() => {
    const bs = pistes.map((p) => bornesDe(p.points)).filter((b): b is BornesRejeu => b !== null)
    return bs.length === 0 ? null : { debut: Math.min(...bs.map((b) => b.debut)), fin: Math.max(...bs.map((b) => b.fin)) }
  }, [pistes])
  const erreurs = ids.filter((id) => resultats[id] && !resultats[id]!.ok)
  const aucunePosition = etat === 'pret' && pistes.length > 0 && !bornes
  const multi = pistes.length > 1

  /* ───── Horloge du rejeu ───── */
  useEffect(() => {
    if (!lecture || !bornes) return
    let dernier = performance.now()
    const id = setInterval(() => {
      const maintenantPerf = performance.now()
      const dt = maintenantPerf - dernier
      dernier = maintenantPerf
      // L'accélération des arrêts ne joue que si TOUS les véhicules sont à l'arrêt ou sans signal à cet instant.
      const tous = pistes.length > 0 && pistes.every((p) => {
        const r = interpoler(p.points, tRef.current, COUPURE_S)
        return !r || r.enArret || r.horsSignal || tRef.current < p.points[0]!.t || tRef.current >= p.points[p.points.length - 1]!.t
      })
      const r = avancer(tRef.current, dt, vitesse, bornes, { accelererArrets: accelerer, enArret: tous })
      tRef.current = r.t
      setT(r.t)
      if (r.fini) setLecture(false)
    }, 50)
    return () => clearInterval(id)
  }, [lecture, bornes, vitesse, accelerer, pistes])

  const sauter = useCallback((instant: number) => {
    tRef.current = instant
    setT(instant)
  }, [])

  const basculerLecture = () => {
    if (!bornes) return
    if (!lecture && tRef.current >= bornes.fin) sauter(bornes.debut)
    setLecture((x) => !x)
  }

  const choisirAdresse = useCallback(
    async (lat: number, lng: number) => {
      if (!source) return
      const cle = `${lat.toFixed(4)},${lng.toFixed(4)}`
      setAdresses((a) => (a[cle] !== undefined ? a : { ...a, [cle]: 'chargement' }))
      const texte = await source.adresse(lat, lng)
      setAdresses((a) => ({ ...a, [cle]: texte }))
    },
    [source],
  )
  const adresseDe = (lat: number, lng: number) => adresses[`${lat.toFixed(4)},${lng.toFixed(4)}`]

  /* ───── Sélection ───── */
  const basculerChoix = (id: string) =>
    setSelection((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= MAX_VEHICULES ? s : [...s, id]))

  /* ───── Exports ───── */
  const jourFichier = formatJour((periodeDemandee?.au ?? Date.now() / 1000) * 1000).split('/').reverse().join('-')
  const exporter = (quoi: 'trajets' | 'arrets' | 'synthese' | 'gpx') => {
    const jeu = pistes
    if (quoi === 'gpx') {
      const arrets = jeu.flatMap((p) => p.arrets.map((arret) => ({ vehicule: p.libelle, arret })))
      telecharger(nomFichier(multi ? 'historique-flotte' : jeu[0]?.libelle ?? 'historique', 'gpx', jourFichier), versGpx(jeu.map((p) => ({ nom: p.libelle, points: p.points })), arrets, COUPURE_S), 'application/gpx+xml')
      return
    }
    if (quoi === 'synthese') {
      const lignes = jeu.flatMap((p) => { const r = resultats[p.id]; return r && r.ok ? [{ vehicule: p.libelle, resume: r.donnees.rapports.resume }] : [] })
      telecharger(nomFichier(multi ? 'synthese-flotte' : `synthese-${jeu[0]?.libelle ?? ''}`, 'csv', jourFichier), csvSynthese(lignes), 'text/csv;charset=utf-8')
      return
    }
    const lignes = jeu.flatMap((p) => (quoi === 'trajets' ? [csvTrajets(p.trajets as Trajet[], p.libelle)] : [csvArrets(p.arrets as Arret[], p.libelle)]))
    // Plusieurs véhicules : un seul fichier, l'en-tête n'est gardé qu'une fois.
    const corps = lignes.map((l, i) => (i === 0 ? l : l.split('\r\n').slice(1).join('\r\n'))).join('')
    telecharger(nomFichier(multi ? `${quoi}-flotte` : `${quoi}-${jeu[0]?.libelle ?? ''}`, 'csv', jourFichier), corps, 'text/csv;charset=utf-8')
  }

  /* ───────────────────────────── rendu ───────────────────────────── */

  if (indisponible) return <div className="hg"><div className="hg-carte-bloc hg-vide">{indisponible}</div></div>
  if (erreurInfo) {
    return (
      <div className="hg">
        <div className="hg-note hg-rouge" role="alert"><Ic d={ICONES.info} /><span>{erreurInfo}</span></div>
      </div>
    )
  }
  if (flotte.filter((e) => e.actif).length === 0) {
    return (
      <div className="hg">
        <div className="hg-carte-bloc hg-vide">
          <b>Aucun boîtier GPS pour l'instant</b>
          <span>L'historique se construit à partir des positions des boîtiers. Rattachez un boîtier à un véhicule ou à un livreur pour voir ses trajets ici.</span>
        </div>
      </div>
    )
  }

  const instantAffiche = bornes ? Math.min(Math.max(t, bornes.debut), bornes.fin) : 0
  // La fin d'une période « hier » est minuit : on compare avec la dernière seconde incluse.
  const memeJour = periodeDemandee ? formatJour(periodeDemandee.du * 1000) === formatJour((periodeDemandee.au - 1) * 1000) : true
  const moment = (s: number) => (memeJour ? formatHeure(s * 1000) : formatDateHeure(s * 1000))
  const resumeTitre = periodeDemandee ? `${formatDateHeure(periodeDemandee.du * 1000)} → ${formatDateHeure(periodeDemandee.au * 1000)} (heure de Bamako)` : ''

  return (
    <div className="hg">
      {/* ───── Quoi, quand ───── */}
      <section className="hg-carte-bloc hg-no-print" aria-label="Choix du véhicule et de la période">
        <div className="hg-choix">
          <div>
            <h2 className="hg-titre">
              Qui afficher ?
              <span className="hg-sous">jusqu'à {MAX_VEHICULES} à la fois, chacun dans sa couleur</span>
            </h2>
            <div className="hg-segs" role="tablist" aria-label="Afficher par">
              {(['vehicule', 'livreur', 'boitier'] as const).map((g) => (
                <button key={g} type="button" role="tab" aria-selected={genre === g} onClick={() => setGenre(g)}>
                  {LIBELLE_GENRE_PL[g]}
                </button>
              ))}
            </div>
            <div className="hg-chips" role="group" aria-label={LIBELLE_GENRE_PL[genre]} style={{ marginTop: 10 }}>
              {entrees.length === 0 && <span className="hg-muet">Aucun {LIBELLE_GENRE[genre].toLowerCase()} avec un boîtier GPS.</span>}
              {entrees.map((e) => {
                const coche = selection.includes(e.boitierId)
                const plein = !coche && selection.length >= MAX_VEHICULES
                const rang = selection.indexOf(e.boitierId)
                return (
                  <label key={`${e.genre}-${e.boitierId}-${e.libelle}`} className="hg-chip" title={!e.actif ? 'Boîtier désactivé' : plein ? `${MAX_VEHICULES} au plus à la fois` : undefined}>
                    <input type="checkbox" checked={coche} disabled={plein || !e.actif} onChange={() => basculerChoix(e.boitierId)} />
                    <span>
                      {coche && <i className="hg-pastille" style={{ background: COULEURS_VEHICULES[rang % COULEURS_VEHICULES.length] }} />}
                      {e.libelle}
                      {e.detail && <small>{e.detail}</small>}
                    </span>
                  </label>
                )
              })}
            </div>
          </div>

          <div>
            <h2 className="hg-titre">Quelle période ? <span className="hg-sous">heure de Bamako</span></h2>
            <div className="hg-periode">
              <div className="hg-segs" role="group" aria-label="Période">
                {(['aujourdhui', 'hier', '7j'] as const).map((p) => (
                  <button key={p} type="button" aria-pressed={preset === p} onClick={() => setPreset(p)}>{LIBELLE_PRESET[p]}</button>
                ))}
                <button type="button" aria-pressed={preset === 'perso'} onClick={() => setPreset('perso')}>Personnalisée</button>
              </div>
              {preset === 'perso' && (
                <>
                  <div className="hg-champ">
                    <label htmlFor="hg-du">Du</label>
                    <input id="hg-du" type="datetime-local" value={duChamp} onChange={(e) => setDuChamp(e.target.value)} />
                  </div>
                  <div className="hg-champ">
                    <label htmlFor="hg-au">Au</label>
                    <input id="hg-au" type="datetime-local" value={auChamp} onChange={(e) => setAuChamp(e.target.value)} />
                  </div>
                </>
              )}
              <button type="button" className="hg-btn hg-plein" onClick={lancer} disabled={selection.length === 0 || etat === 'chargement' || !info}>
                <Ic d={ICONES.recharger} /> {etat === 'chargement' ? 'Chargement…' : 'Afficher'}
              </button>
            </div>
          </div>

          {info && (
            <div className="hg-note" role="note">
              <Ic d={ICONES.info} />
              <span>
                Historique conservé <b>{info.retentionJours} jours</b> (palier {LIBELLE_PALIER[info.palier]}), soit depuis le{' '}
                <b>{formatDateHeure(info.disponibleDepuis * 1000)}</b> ; {info.periodeMaxJours} jours au plus par demande.
                {info.palier === 'starter' && ' Les paliers Pro et Business gardent l’historique plus longtemps.'}
              </span>
            </div>
          )}
          {erreurPeriode && <div className="hg-note hg-rouge" role="alert"><Ic d={ICONES.info} /><span>{erreurPeriode}</span></div>}
          {!erreurPeriode && messagesPeriode.map((m) => <div key={m} className="hg-note hg-ambre" role="status"><Ic d={ICONES.info} /><span>{m}</span></div>)}
        </div>
      </section>

      {/* ───── Carte et lecteur ───── */}
      <section className="hg-carte-bloc hg-no-print" aria-label="Carte et lecteur de rejeu">
        {etat === 'chargement' && pistes.length === 0 && <div className="hg-squelette" aria-busy="true" aria-label="Chargement de l'historique" style={{ height: 320 }} />}
        {erreurs.map((id) => {
          const r = resultats[id]
          return r && !r.ok ? <div key={id} className="hg-note hg-rouge" role="alert" style={{ marginBottom: 10 }}><Ic d={ICONES.info} /><span><b>{libelleDe(id)}</b> : {r.erreur}</span></div> : null
        })}
        {(pistes.length > 0 || etat === 'chargement') && (
          <div style={{ position: 'relative' }}>
            <Suspense fallback={<div className="hg-squelette" aria-busy="true" style={{ height: 320 }} />}>
              <Carte
                pistes={pistes}
                t={bornes ? instantAffiche : null}
                couleurs={multi ? 'vehicule' : 'vitesse'}
                suivre={suivre}
                surClicTrace={(_id, instant) => sauter(instant)}
                surChoixArret={(_id, a) => sauter(a.debut)}
                cleCadrage={cleCadrage}
                arretOuvert={arretOuvert}
              />
            </Suspense>
            {etat === 'chargement' && <div className="hg-voile" role="status">Chargement de l'historique…</div>}
            {aucunePosition && <div className="hg-voile" role="status">Aucune position reçue sur cette période.</div>}
          </div>
        )}
        {etat === 'pret' && pistes.length === 0 && erreurs.length === 0 && <div className="hg-vide"><b>Rien à afficher</b><span>Choisissez au moins un véhicule, un livreur ou un boîtier.</span></div>}

        {bornes && (
          <div className="hg-lecteur" style={{ marginTop: 12 }}>
            <div className="hg-barre">
              <button type="button" className="hg-btn" onClick={() => { sauter(bornes.debut); setLecture(false) }} aria-label="Revenir au début"><Ic d={ICONES.debut} /></button>
              <button type="button" className="hg-btn hg-plein" onClick={basculerLecture} aria-label={lecture ? 'Pause' : 'Lecture'}>
                <Ic d={lecture ? ICONES.pause : ICONES.lecture} /> {lecture ? 'Pause' : 'Lecture'}
              </button>
              <button type="button" className="hg-btn" onClick={() => { sauter(bornes.fin); setLecture(false) }} aria-label="Aller à la fin"><Ic d={ICONES.fin} /></button>
              <div className="hg-vit" role="group" aria-label="Vitesse du rejeu">
                {VITESSES_REJEU.map((v) => <button key={v} type="button" aria-pressed={vitesse === v} onClick={() => setVitesse(v)}>×{v}</button>)}
              </div>
              <span className="hg-heure" aria-live="off">{formatDateHeure(instantAffiche * 1000)}:{String(Math.floor(instantAffiche) % 60).padStart(2, '0')}</span>
            </div>
            <input
              className="hg-range" type="range" min={bornes.debut} max={bornes.fin} step={1} value={instantAffiche}
              aria-label="Position dans la période" aria-valuetext={formatHeureSeconde(instantAffiche * 1000)}
              onChange={(e) => sauter(Number(e.target.value))}
            />
            <div>
              {pistes.map((p) => {
                const r = resultats[p.id]
                return r && r.ok ? <Frise key={p.id} debut={bornes.debut} fin={bornes.fin} rapports={r.donnees.rapports} t={instantAffiche} surChoix={sauter} /> : null
              })}
              <div className="hg-frise-lib">
                <span><i style={{ background: 'var(--hg-accent-vif)' }} />Trajet</span>
                <span><i style={{ background: '#aab6b1' }} />Arrêt</span>
                <span className="hg-muet">Cliquez la frise ou le tracé pour sauter à un instant.</span>
              </div>
            </div>
            <div className="hg-barre">
              <label className="hg-case"><input type="checkbox" checked={accelerer} onChange={(e) => setAccelerer(e.target.checked)} />Accélérer les arrêts (×20)</label>
              <label className="hg-case"><input type="checkbox" checked={suivre} onChange={(e) => setSuivre(e.target.checked)} />Suivre le véhicule</label>
              <span className="hg-muet" style={{ fontSize: 12.5 }}>Rejeu complet à ×{vitesse} : {formatDuree(dureeReelleRejeu(bornes, vitesse))}</span>
            </div>
            <div className="hg-infos" aria-live="polite">
              {pistes.map((p) => {
                const r = interpoler(p.points, instantAffiche, COUPURE_S)
                if (!r) return null
                const adr = adresseDe(r.lat, r.lng)
                return (
                  <div className="hg-info" key={p.id}>
                    <b><i className="hg-pastille" style={{ background: p.couleur }} />{p.libelle}</b>
                    <div className="hg-ligne">
                      <span>{formatHeureSeconde(instantAffiche * 1000)}</span>
                      <span>{r.horsSignal ? 'Pas de signal' : formatVitesse(r.v)}</span>
                      <span>Moteur : {r.ign === null ? 'inconnu' : r.ign ? 'allumé' : 'éteint'}</span>
                      {r.enArret && <span className="hg-pil">À l'arrêt</span>}
                      {r.horsSignal && <span className="hg-pil hg-ambre">Position non reçue</span>}
                    </div>
                    <div className="hg-adresse">
                      {r.lat.toFixed(5)}, {r.lng.toFixed(5)}
                      {' · '}
                      {adr === 'chargement' ? 'Recherche de l’adresse…' : adr ? adr : adr === null ? 'Adresse non disponible' : (
                        <button type="button" className="hg-btn hg-petit" onClick={() => void choisirAdresse(r.lat, r.lng)}>Afficher l’adresse</button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
            {pistes.some((p) => resultats[p.id]?.ok && (resultats[p.id] as { ok: true; donnees: DonneesBoitier }).donnees.historique.retenus < (resultats[p.id] as { ok: true; donnees: DonneesBoitier }).donnees.historique.total) && (
              <p className="hg-muet" style={{ fontSize: 12, margin: 0 }}>
                Le tracé est simplifié pour rester fluide ; les rapports ci-dessous sont calculés sur toutes les positions reçues.
              </p>
            )}
          </div>
        )}
      </section>

      {/* ───── Rapports ───── */}
      {etat === 'pret' && pistes.length > 0 && (
        <section className="hg-carte-bloc hg-imprimable" aria-label="Rapports de la période">
          <div className="hg-impression-titre">
            <h2>Historique des trajets — {nomOrganisation}</h2>
            <div>{resumeTitre}</div>
          </div>
          <div className="hg-barre hg-no-print" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="hg-segs" role="tablist" aria-label="Rapport">
              {([['synthese', 'Synthèse'], ['trajets', 'Trajets'], ['arrets', 'Arrêts']] as const).map(([id, lib]) => (
                <button key={id} type="button" role="tab" aria-selected={onglet === id} onClick={() => setOnglet(id)}>{lib}</button>
              ))}
            </div>
            <div className="hg-actions">
              <button type="button" className="hg-btn hg-petit" onClick={() => exporter(onglet === 'synthese' ? 'synthese' : onglet)}><Ic d={ICONES.telecharger} /> CSV</button>
              <button type="button" className="hg-btn hg-petit" onClick={() => exporter('gpx')}><Ic d={ICONES.telecharger} /> GPX</button>
              <button type="button" className="hg-btn hg-petit" onClick={() => window.print()}><Ic d={ICONES.imprimer} /> Imprimer / PDF</button>
            </div>
          </div>
          <p className="hg-muet" style={{ margin: '0 0 12px', fontSize: 12.5 }}>{resumeTitre}</p>

          {onglet === 'synthese' && <Synthese pistes={pistes} resultats={resultats} surOuvrirAlertes={surOuvrirAlertes} />}
          {onglet === 'trajets' && (
            <>
              {pistes.map((p) => (
                <div key={p.id}>
                  {multi && <div className="hg-vehicule-t"><i className="hg-pastille" style={{ background: p.couleur }} />{p.libelle}</div>}
                  {p.trajets.length === 0 ? <div className="hg-vide"><b>Aucun trajet</b><span>Le véhicule n'a pas roulé sur cette période (ou moins de 100 m).</span></div> : (
                    <table className="hg-table">
                      <thead><tr><th>N°</th><th>Départ</th><th>Arrivée</th><th className="hg-num">Durée</th><th className="hg-num">Distance</th><th className="hg-num">Vitesse moy.</th><th className="hg-num">Vitesse max.</th></tr></thead>
                      <tbody>
                        {p.trajets.map((x) => (
                          <tr key={x.n} className="hg-ligne-clic" onClick={() => sauter(x.debut)} title="Aller au départ de ce trajet">
                            <td data-label="N°"><span className="hg-v">{x.n}</span></td>
                            <td data-label="Départ"><span className="hg-v">{moment(x.debut)}{x.lieuDebut ? ` · ${x.lieuDebut}` : ''}</span></td>
                            <td data-label="Arrivée"><span className="hg-v">{moment(x.fin)}{x.lieuFin ? ` · ${x.lieuFin}` : ''}</span></td>
                            <td className="hg-num" data-label="Durée"><span className="hg-v">{formatDuree(x.dureeS)}</span></td>
                            <td className="hg-num" data-label="Distance"><span className="hg-v">{formatDistance(x.distanceM)}</span></td>
                            <td className="hg-num" data-label="Vitesse moy."><span className="hg-v">{formatVitesse(x.vitesseMoyKmh)}</span></td>
                            <td className="hg-num" data-label="Vitesse max."><span className="hg-v">{formatVitesse(x.vitesseMaxKmh)}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </>
          )}
          {onglet === 'arrets' && (
            <>
              {pistes.map((p) => (
                <div key={p.id}>
                  {multi && <div className="hg-vehicule-t"><i className="hg-pastille" style={{ background: p.couleur }} />{p.libelle}</div>}
                  {p.arrets.length === 0 ? <div className="hg-vide"><b>Aucun arrêt</b><span>Aucun arrêt de 3 minutes ou plus sur cette période.</span></div> : (
                    <table className="hg-table">
                      <thead><tr><th>N°</th><th>Début</th><th>Fin</th><th className="hg-num">Durée</th><th>Lieu</th><th>Moteur</th></tr></thead>
                      <tbody>
                        {p.arrets.map((a) => {
                          const adr = adresseDe(a.lat, a.lng)
                          return (
                            <tr key={a.n} className="hg-ligne-clic" onClick={() => { sauter(a.debut); setArretOuvert({ pisteId: p.id, n: a.n }) }} title="Voir cet arrêt sur la carte">
                              <td data-label="N°"><span className="hg-v">{a.n}</span></td>
                              <td data-label="Début"><span className="hg-v">{moment(a.debut)}</span></td>
                              <td data-label="Fin"><span className="hg-v">{moment(a.fin)}</span></td>
                              <td className="hg-num" data-label="Durée"><span className="hg-v">{formatDuree(a.dureeS)}</span></td>
                              <td data-label="Lieu"><span className="hg-v">
                                {a.lieu ?? `${a.lat.toFixed(5)}, ${a.lng.toFixed(5)}`}
                                {adr && adr !== 'chargement' && <div className="hg-muet">{adr}</div>}
                                {adr === undefined && <> <button type="button" className="hg-btn hg-petit hg-no-print" onClick={(e) => { e.stopPropagation(); void choisirAdresse(a.lat, a.lng) }}>Adresse</button></>}
                                {adr === 'chargement' && <div className="hg-muet">Recherche…</div>}
                              </span></td>
                              <td data-label="Moteur"><span className="hg-v">
                                {a.moteurAllume === null ? <span className="hg-pil">Inconnu</span> : a.moteurAllume ? <span className="hg-pil hg-ambre">Allumé · {formatDuree(a.moteurAllumeS)}</span> : <span className="hg-pil hg-vert">Éteint</span>}
                              </span></td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </>
          )}
        </section>
      )}
    </div>
  )
}

/* ───────────────────────────── Synthèse ───────────────────────────── */

function Synthese({ pistes, resultats, surOuvrirAlertes }: { pistes: readonly Piste[]; resultats: Record<string, ResultatBoitier>; surOuvrirAlertes: (() => void) | undefined }) {
  return (
    <>
      {pistes.map((p) => {
        const r = resultats[p.id]
        if (!r || !r.ok) return null
        const s = r.donnees.rapports.resume
        return (
          <div key={p.id}>
            {pistes.length > 1 && <div className="hg-vehicule-t"><i className="hg-pastille" style={{ background: p.couleur }} />{p.libelle}</div>}
            {s.nbPositions === 0 ? (
              <div className="hg-vide"><b>Aucune position</b><span>Aucune position n'a été reçue de ce boîtier sur cette période.</span></div>
            ) : (
              <div className="hg-kpis">
                <div className="hg-kpi"><span>Distance totale</span><strong>{formatDistance(s.distanceM)}</strong><small>{s.nbTrajets} trajet{s.nbTrajets > 1 ? 's' : ''}</small></div>
                <div className="hg-kpi"><span>Temps de conduite</span><strong>{formatDuree(s.conduiteS)}</strong><small>moyenne {formatVitesse(s.vitesseMoyKmh)}</small></div>
                <div className="hg-kpi"><span>Temps à l'arrêt</span><strong>{formatDuree(s.arretS)}</strong><small>{s.nbArrets} arrêt{s.nbArrets > 1 ? 's' : ''}{s.arretMoteurAllumeS > 0 ? ` · dont ${formatDuree(s.arretMoteurAllumeS)} moteur allumé` : ''}</small></div>
                <div className="hg-kpi"><span>Vitesse maximale</span><strong>{formatVitesse(s.vitesseMaxKmh)}</strong></div>
                {s.sansSignalS > 0 && <div className="hg-kpi"><span>Sans signal</span><strong>{formatDuree(s.sansSignalS)}</strong><small>aucune position reçue</small></div>}
                <div className="hg-kpi">
                  <span>Alertes de la période</span>
                  {s.alertesDisponibles ? (
                    <>
                      <strong>{s.nbAlertes ?? 0}</strong>
                      {surOuvrirAlertes && <small><button type="button" className="hg-btn hg-petit hg-no-print" onClick={surOuvrirAlertes}>Historique des alertes</button></small>}
                    </>
                  ) : (
                    <>
                      <strong>—</strong>
                      <small>Les alertes GPS sont réservées aux paliers Pro et Business.</small>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}
